import type { FC } from 'react';
import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';
import { SubtleLink } from '../SubtleLink.tsx';

const i18n = defineMessages({
	anonymousUser: {
		id: 'feed.card.anonymousUser',
		defaultMessage: 'Anonymous User',
		description: 'The text is shown as the display name in the activity line of a feed card when the author of the activity is anonymous (i.e. their identity is not available or hidden).',
	},
	displayName: {
		id: 'feed.card.displayName',
		// eslint-disable-next-line @atlassian/i18n/no-useless-message
		defaultMessage: '{name}',
		description:
			'Link in feed card activity line showing the user who performed the activity. {name} = user display name.',
	},
	createAction: {
		id: 'feed.card.createAction',
		defaultMessage: 'created',
		description: 'Text that shows a user created a new page/blog',
	},
	editAction: {
		id: 'feed.card.editAction',
		defaultMessage: `{num, plural,
      one {edited}
      other {edited {num} times}}`,
		description: 'Text for the edit action summary text at the top of the card',
	},
	updateAction: {
		id: 'feed.card.updateAction',
		defaultMessage: `{num, plural,
			one {updated}
			other {updated {num} times}}`,
		description: 'Text for the update action summary text at the top of the card',
	},
	publishLiveAction: {
		id: 'feed.card.publishAction',
		defaultMessage: 'published',
		description: 'Text that shows a user published a live page',
	},
	commentAction: {
		id: 'feed.card.commentAction',
		defaultMessage: `{num, plural,
      one {commented}
      other {commented {num} times}}`,
		description: 'Text for the comment action summary text at the top of the card',
	},
	unknownAction: {
		id: 'feed.card.unknownAction',
		defaultMessage: 'Unknown update',
		description: 'fallback text to display for an action that has an unknown type.',
	},
});

type ActivityDescriptorContainerProps = {
	activityDropdownLineItem: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameWrapper = styled.div({
	color: token('color.text'),
	fontWeight: token('font.weight.medium'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	marginRight: token('space.050'),
});

const ActivityDescriptorContainer =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div<ActivityDescriptorContainerProps>({
		display: 'flex',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		maxWidth: (props) => (props.activityDropdownLineItem ? '270px' : 'inherit'),
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionWrapper = styled.div({
	maxWidth: 'fit-content',
});

type FeedCardActivityLineProps = {
	type:
		| 'COMMENT'
		| 'CREATE'
		| 'EDIT'
		| 'EDITLIVE'
		| 'PUBLISH'
		| 'PUBLISHLIVE'
		| 'SNAPSHOT'
		| 'START'
		| '%future added value'; // for compatibility with Relay -- should be deleted from BE code
	userDisplayName: string | null;
	link: string;
	groupSize: number;
	activityDropdownLineItem: boolean;
};

// This component is used by both feed activities (FeedContentQuery's FeedEventType) and recent actions activities (FeedCardAllActivityQuery's PageActivityAction, mapped in activityLineHelpers.ts to match FeedEventType)
const ActivityLineUpdateLinkComponent: FC<FeedCardActivityLineProps & WrappedComponentProps> = ({
	type,
	userDisplayName,
	link,
	groupSize,
	intl,
	activityDropdownLineItem,
}) => {
	const displayName = userDisplayName || intl.formatMessage(i18n.anonymousUser);
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const [showNameWithTooltip, setShowNameWithTooltip] = useState(false);
	const activityDescriptorNode = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		// Check if the displayName is truncated before adding tooltip; 270 represents the available
		// flex area of the parent div (fixed width from dropdown)
		if (
			activityDescriptorNode.current &&
			activityDescriptorNode.current.scrollWidth >= 270 &&
			activityDropdownLineItem
		) {
			setShowNameWithTooltip(true);
		}
	}, [activityDropdownLineItem]);

	const handleClick = useCallback(() => {
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'feedCardActivityLink',
			includeNavdex: true,
			additionalAttributes: {
				activityType: type,
				isDropdown: activityDropdownLineItem,
			},
		});
	}, [type, activityDropdownLineItem, fireAnalyticsEvent]);

	const renderButtonBody = () => {
		const i18nValues = {
			num: groupSize,
		};
		switch (type) {
			case 'CREATE':
				return <FormattedMessage {...i18n.createAction} />;
			case 'EDIT':
				return <FormattedMessage {...i18n.updateAction} values={i18nValues} />;
			case 'EDITLIVE':
				return <FormattedMessage {...i18n.editAction} values={i18nValues} />;
			case 'PUBLISHLIVE':
				return <FormattedMessage {...i18n.publishLiveAction} />;
			case 'COMMENT':
				return <FormattedMessage {...i18n.commentAction} values={i18nValues} />;
			default:
				return <FormattedMessage {...i18n.unknownAction} />;
		}
	};

	const i18nNameValues = {
		name: displayName,
	};

	const activityDescriptor = (
		<ActivityDescriptorContainer
			activityDropdownLineItem={activityDropdownLineItem}
			ref={activityDescriptorNode}
		>
			<NameWrapper>
				<FormattedMessage {...i18n.displayName} values={i18nNameValues} />
			</NameWrapper>
			<ActionWrapper>{renderButtonBody()}</ActionWrapper>
		</ActivityDescriptorContainer>
	);

	const activityDescriptorTooltip = (
		<Tooltip
			content={<FormattedMessage {...i18n.displayName} values={i18nNameValues} />}
			testId={displayName}
		>
			{activityDescriptor}
		</Tooltip>
	);

	return (
		<SubtleLink href={link} onClick={handleClick} testId={`${displayName}-${type}-${groupSize}`}>
			{showNameWithTooltip ? activityDescriptorTooltip : activityDescriptor}
		</SubtleLink>
	);
};

export const ActivityLineUpdateLink = injectIntl(ActivityLineUpdateLinkComponent);
